import React, { useMemo, useEffect } from 'react';
import { SearchField } from '@sas/nova-commons/components/SearchField';
import { Item, List } from '@sas/nova-commons/components/List';
import { useListState } from '@sas/nova-commons/components/List/useListState';
import { useRenderEffect } from '../hooks';
import { useParentSize } from '@sas/nova-commons/hooks/useParentSize';
import cn from './ListFix.css.json';
import './ListFix.css';
// Utility functions to make the the conversion between list index and row key searchable
const IndexMapping = {
    toRowKey(i) {
        return i.toString();
    },
    fromRowKey(i) {
        return parseInt(i);
    },
};
export function SearchableList(props) {
    const { filteredSelections, searchResults, selectedSearchResults } = useMemo(() => {
        var _a, _b;
        const allSelectedIndices = new Set(props.selection); // Portable selections
        const filteredSelections = new Set(allSelectedIndices); // Portable selections filtered from the search result
        const searchResults = []; // Maps list index -> value index
        const selectedSearchResults = new Set(); // Selected rows in list
        const searchPattern = props.searchValue.toLocaleLowerCase();
        for (const [index, value] of (_b = (_a = props.values) === null || _a === void 0 ? void 0 : _a.entries()) !== null && _b !== void 0 ? _b : []) {
            if (value.toLocaleLowerCase().includes(searchPattern)) {
                const listIndex = IndexMapping.toRowKey(searchResults.length);
                searchResults.push(index);
                if (allSelectedIndices.has(index)) {
                    filteredSelections.delete(index);
                    selectedSearchResults.add(listIndex);
                }
            }
        }
        return {
            filteredSelections,
            searchResults,
            selectedSearchResults,
        };
    }, [props.selection, props.values, props.searchValue]);
    const [setParentRef, dimensions] = useParentSize();
    const listState = useListState();
    const clearMeasurementCache = listState.clearMeasurementCache;
    useEffect(() => {
        clearMeasurementCache({ startRowIndex: 0, startColumnIndex: 0 });
    }, [props.itemHeight, clearMeasurementCache]);
    // Sync portable -> listState
    const needsRerender = useRenderEffect(() => {
        listState.setSelectRows({ selectedRows: selectedSearchResults });
    }, [selectedSearchResults]);
    if (needsRerender) {
        // Rerender queued, bail for performance
        return null;
    }
    else {
        const list = (React.createElement("div", { style: { height: '100%', minHeight: 0 } },
            React.createElement("div", { ref: setParentRef, style: { width: props.preferredWidth, height: props.preferredHeight } }, dimensions ? (React.createElement(List, Object.assign({}, listState, { 
                // Use whole numbers as a workaround to NOVACOMMONS-21780
                width: Math.floor(dimensions.width), height: Math.floor(dimensions.height), itemHeight: props.itemHeight ? () => props.itemHeight : undefined, className: cn.listFix, testId: props.listTestId, getRowKey: IndexMapping.toRowKey, getRowIndex: IndexMapping.fromRowKey, 
                // Sync user actions -> portable
                onSelect: function onSelect(selectedRows) {
                    var _a;
                    const selectedIndexes = [];
                    for (const row of selectedRows !== null && selectedRows !== void 0 ? selectedRows : []) {
                        const index = searchResults[IndexMapping.fromRowKey(row)];
                        if (index !== undefined) {
                            selectedIndexes.push(index);
                        }
                    }
                    // Preserve filtered selections
                    if (props.type === 'multi') {
                        for (const index of filteredSelections) {
                            selectedIndexes.push(index);
                        }
                    }
                    // Portable expects sorted results
                    (_a = props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, selectedIndexes.sort((a, b) => a - b));
                }, selectionMode: props.type === 'multi' ? 'toggle' : 'singleRadio', rowCount: searchResults.length, tableTheme: () => {
                    var _a, _b;
                    return {
                        '--table-bg': (_b = (_a = props.styleState) === null || _a === void 0 ? void 0 : _a.normal) === null || _b === void 0 ? void 0 : _b.bg,
                    };
                }, theme: (_dt) => {
                    var _a;
                    const { normal, font } = (_a = props.styleState) !== null && _a !== void 0 ? _a : {};
                    return {
                        '--list-item-bg': normal === null || normal === void 0 ? void 0 : normal.bg,
                        '--list-item-fg': normal === null || normal === void 0 ? void 0 : normal.fg,
                        '--list-item-description-fg': normal === null || normal === void 0 ? void 0 : normal.fg,
                        '--list-item-default-fg': normal === null || normal === void 0 ? void 0 : normal.fg,
                        '--list-item-default-description-fg': normal === null || normal === void 0 ? void 0 : normal.fg,
                        '--list-item-font-family': font === null || font === void 0 ? void 0 : font.fontFamily,
                        '--list-item-font-style': font === null || font === void 0 ? void 0 : font.fontStyle,
                        '--list-item-font-weight-default': font === null || font === void 0 ? void 0 : font.fontWeight,
                        '--list-item-font-size-m': font === null || font === void 0 ? void 0 : font.fontSize,
                        '--list-item-font-size-s': 'var(--list-item-font-size-m)', // measure text should be the same size
                    };
                }, itemColumn: {
                    key: 'itemColumn',
                    item(index) {
                        var _a, _b;
                        const originalIndex = searchResults[index];
                        if (originalIndex === undefined || !((_a = props.values) === null || _a === void 0 ? void 0 : _a[originalIndex])) {
                            return null;
                        }
                        const measure = (_b = props.measureValues) === null || _b === void 0 ? void 0 : _b[originalIndex];
                        return (React.createElement(Item, { title: props.values[originalIndex], info: measure !== undefined ? { infoText: measure, hasPriority: true } : null }));
                    },
                }, ariaLabel: props.accessibilityLabel }))) : null)));
        return (React.createElement("div", { style: {
                minHeight: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }, tabIndex: -1 },
            props.searchable ? (React.createElement("div", { style: { padding: '0.1875rem 0 0.1875rem 0.5rem', display: 'flex' } },
                React.createElement(SearchField, { filterMode: true, value: props.searchValue, numberOfResults: props.searchValue ? searchResults.length : null, onChange: (e) => {
                        props.onSearchValueChange(e.value);
                    } }))) : null,
            props.renderListWrapper ? props.renderListWrapper(list) : list));
    }
}
